import type { DefaultOptions, NormalizedCacheObject } from '@apollo/client';
import { ApolloClient, ApolloLink } from '@apollo/client';
import cache, { tryRestore } from './cache';
import getTransportLink from './getTransportLink';
import parseJsonLink from './parseJsonLink';
import authTokenUtil from '../../account/authTokenUtil';
import { isPublicViewPath } from '../../utils/useIsPublicView';

let singleton: {
  client: ApolloClient<NormalizedCacheObject>;
  precedingLinks: ApolloLink[];
} | null = null;
const getApolloClient = () => singleton?.client;

export function updateApolloLink(
  options: Parameters<typeof getTransportLink>[0],
) {
  singleton?.client.setLink(
    ApolloLink.from([...singleton.precedingLinks, getTransportLink(options)]),
  );
}

export const apolloClientDefaultOptions: DefaultOptions = {
  watchQuery: {
    fetchPolicy: 'cache-and-network',
    // After Apollo 3.1, 'cache-and-network` means whenever the cached value changes, a new network request is triggered.
    // in order to avoid this unnecessary effect (which could even lead to infinite loops), nextFetchPolicy is needed.
    // recommended setting to from https://github.com/apollographql/apollo-client/pull/6712#issuecomment-679446404
    nextFetchPolicy(lastFetchPolicy, { reason, initialFetchPolicy }) {
      // When variables change, the default behavior is to reset
      // options.fetchPolicy to context.initialFetchPolicy when nextFetchPolicy is not provided.
      // Therefore we need to handle this case explicitly here, since we want to requery when the variables change.
      if (reason === 'variables-changed') {
        return initialFetchPolicy;
      }

      if (
        lastFetchPolicy === 'cache-and-network' ||
        lastFetchPolicy === 'network-only'
      ) {
        return 'cache-first';
      }
      return lastFetchPolicy;
    },
  },
};

interface CreateApolloClientOptions {
  errorLink?: ApolloLink;
}
export async function createApolloClient({
  errorLink,
}: CreateApolloClientOptions = {}) {
  const isPublicView = isPublicViewPath(window.location.pathname);
  if (!isPublicView && authTokenUtil.get() != null) {
    await tryRestore();
  }

  const client = new ApolloClient({
    cache,
    assumeImmutableResults: true,
    connectToDevTools: process.env.NODE_ENV !== 'production',
    defaultOptions: apolloClientDefaultOptions,
  });

  const precedingLinks: ApolloLink[] = [parseJsonLink];
  if (errorLink) {
    precedingLinks.unshift(errorLink);
  }
  if (
    process.env.NODE_ENV !== 'production' &&
    process.env.NODE_ENV !== 'test'
  ) {
    precedingLinks.unshift((await import('apollo-link-logger')).default);
  }

  singleton = { client, precedingLinks };

  updateApolloLink({ authToken: null, socket: null });
  return client;
}
export default getApolloClient;
